import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { mergeFilterUpdate } from '#/modules/commonComponents/table/listFilterKeys';
import {
  buildListSearchParams,
  parseListSearchParams,
} from '#/utils/listUrl/parseListSearchParams';

/** 帳號／職務筆數少，預設一頁 20 筆（URL 未帶 page_size 時）。 */
export const ACCESS_LIST_PAGE_SIZE = 20;

const EMPTY_PAGE = {
  current_page: 1,
  page_size: ACCESS_LIST_PAGE_SIZE,
  total_count: 0,
  total_page: 1,
};

/**
 * 帳號／職務列表共用：URL 查詢、預設排序、每頁 20 筆。
 *
 * @param {object} options
 * @param {readonly unknown[]} options.queryKeyBase
 * @param {(listQuery: object) => Promise<{data: object[], page: object}>} options.fetchList
 * @param {Array<{id: string, desc: boolean}>} options.defaultSort
 */
function useAccessList({ queryKeyBase, fetchList, defaultSort }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const listQuery = useMemo(() => {
    const parsed = parseListSearchParams(searchParams);
    return searchParams.has('page_size')
      ? parsed
      : { ...parsed, pageSize: ACCESS_LIST_PAGE_SIZE };
  }, [searchParams]);

  const apiQuery = useMemo(() => (
    listQuery.sort.length > 0 ? listQuery : { ...listQuery, sort: defaultSort }
  ), [listQuery, defaultSort]);

  const query = useQuery({
    queryKey: [...queryKeyBase, apiQuery],
    queryFn: () => fetchList(apiQuery),
    placeholderData: keepPreviousData,
  });

  const setListQuery = useCallback((updater) => {
    const next = typeof updater === 'function' ? updater(listQuery) : updater;
    setSearchParams(buildListSearchParams(next), { replace: true });
  }, [listQuery, setSearchParams]);

  const setPage = useCallback((page) => {
    setListQuery((prev) => ({ ...prev, page }));
  }, [setListQuery]);

  const setPageSize = useCallback((pageSize) => {
    setListQuery((prev) => ({ ...prev, pageSize, page: 1 }));
  }, [setListQuery]);

  const setKeyword = useCallback((keyword) => {
    setListQuery((prev) => ({ ...prev, keyword, page: 1 }));
  }, [setListQuery]);

  const setFilter = useCallback((key, value) => {
    setListQuery((prev) => ({
      ...prev,
      page: 1,
      filters: mergeFilterUpdate(prev.filters, key, value),
    }));
  }, [setListQuery]);

  const setSort = useCallback((sort) => {
    setListQuery((prev) => ({ ...prev, page: 1, sort: sort ?? [] }));
  }, [setListQuery]);

  const removeSortAt = useCallback((index) => {
    setListQuery((prev) => ({
      ...prev,
      page: 1,
      sort: (prev.sort ?? []).filter((_, sortIndex) => sortIndex !== index),
    }));
  }, [setListQuery]);

  const resetListQuery = useCallback(() => {
    setListQuery((prev) => ({
      page: 1,
      pageSize: prev.pageSize,
      keyword: '',
      sort: [],
      filters: {},
    }));
  }, [setListQuery]);

  return {
    rows: query.data?.data ?? [],
    pagination: query.data?.page ?? EMPTY_PAGE,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    listQuery,
    setPage,
    setPageSize,
    setKeyword,
    setFilter,
    setSort,
    removeSortAt,
    resetListQuery,
  };
}

export default useAccessList;
